import React, { useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import { Loader2 } from 'lucide-react';

const FieldLabel = ({ htmlFor, children, required }) => (
  <label
    htmlFor={htmlFor}
    className="block text-[12.5px] font-[560] text-foreground mb-1.5 tracking-[.005em]"
  >
    {children}
    {required && <span className="text-destructive ml-0.5">*</span>}
  </label>
);

const fieldBase =
  'w-full px-3 py-[9px] rounded-lg border border-input text-[13px] text-foreground-strong bg-card placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] disabled:opacity-50 disabled:cursor-not-allowed';

export const NewTest = ({ onSubmit, onClose, initialData, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    max_score: 100,
    duration_minutes: 60,
    passing_score: 70,
    difficulty_level: 'MEDIUM',
    is_active: true,
    ...initialData,
  });

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        duration_minutes: 60,
        passing_score: 70,
        difficulty_level: 'MEDIUM',
      }));
    }
  }, [initialData?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const isEditMode = Boolean(initialData?.id);
  const buttonText = isEditMode ? 'Update Test' : 'Create Test';
  const loadingText = isEditMode ? 'Updating...' : 'Creating...';

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto px-[22px] py-[18px] space-y-4">
        <div className="grid grid-cols-[1fr_132px] gap-[14px]">
          <div>
            <FieldLabel htmlFor="test-name" required>
              Test Name
            </FieldLabel>
            <input
              id="test-name"
              className={fieldBase}
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g. Basics of Excel"
              required
              disabled={loading}
            />
          </div>
          <div>
            <FieldLabel htmlFor="test-max-score" required>
              Max Score
            </FieldLabel>
            <input
              id="test-max-score"
              type="number"
              className={fieldBase}
              value={formData.max_score}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  max_score: parseInt(e.target.value, 10),
                })
              }
              min="1"
              required
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <FieldLabel htmlFor="test-description" required>
            Description
          </FieldLabel>
          <textarea
            id="test-description"
            className={`${fieldBase} min-h-[88px] resize-y leading-relaxed`}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="What this test measures, and how…"
            required
            disabled={loading}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 px-[22px] py-[14px] border-t border-border bg-brand-bg-subtle shrink-0">
        {onClose && (
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {loadingText}
            </>
          ) : (
            buttonText
          )}
        </Button>
      </div>
    </form>
  );
};
